import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, FlaskConical, ChevronDown } from "lucide-react";
import { useAuth, formatAuthError, RESEARCH_FIELDS } from "../context/AuthContext";

export default function Register() {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/products";

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    research_field: "",
    marketing_opt_in: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Already-logged-in visitor → bounce to their original destination.
  useEffect(() => {
    if (isAuthenticated) navigate(redirectTo, { replace: true });
  }, [isAuthenticated, redirectTo, navigate]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.research_field) {
      setError("Please select your research field.");
      return;
    }
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      await register(form);
      // Trigger the one-shot post-signup welcome banner (FIRST10 · 10% off).
      // Global <WelcomeOfferBanner /> in App.js reads this key on next paint.
      try {
        window.localStorage.setItem("luviarx_welcome_first10_v1", "show");
      } catch { /* ignore storage failures */ }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatAuthError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      data-testid="register-page"
      className="min-h-[calc(100vh-7rem)] bg-cream-200/40 py-14 md:py-20"
    >
      <div className="container-x max-w-xl">
        <div className="mb-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mb-5">
            <FlaskConical className="w-5 h-5 text-sage-600" strokeWidth={1.8} />
          </div>
          <p className="overline mb-2">Researcher Account</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-charcoal-600 leading-tight">
            Create your account
          </h1>
          <p className="mt-3 text-sm text-charcoal-400 max-w-md mx-auto">
            Registration is required to purchase research compounds. Verified researchers gain
            access to ordering, batch history, and Certificate of Analysis requests.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="surface-card p-8 md:p-10 space-y-5"
          data-testid="register-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First name" htmlFor="first_name">
              <input
                id="first_name"
                data-testid="register-first-name"
                type="text"
                autoComplete="given-name"
                required
                value={form.first_name}
                onChange={update("first_name")}
                className="input"
              />
            </Field>
            <Field label="Last name" htmlFor="last_name">
              <input
                id="last_name"
                data-testid="register-last-name"
                type="text"
                autoComplete="family-name"
                required
                value={form.last_name}
                onChange={update("last_name")}
                className="input"
              />
            </Field>
          </div>

          <Field label="Email" htmlFor="email">
            <input
              id="email"
              data-testid="register-email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={update("email")}
              className="input"
            />
          </Field>

          <Field label="Password" htmlFor="password" hint="Minimum 8 characters.">
            <input
              id="password"
              data-testid="register-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={form.password}
              onChange={update("password")}
              className="input"
            />
          </Field>

          <Field label="Research field" htmlFor="research_field">
            <div className="relative">
              <select
                id="research_field"
                data-testid="register-research-field"
                required
                value={form.research_field}
                onChange={update("research_field")}
                className="input appearance-none pr-10"
              >
                <option value="" disabled>Select your primary research field…</option>
                {RESEARCH_FIELDS.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-charcoal-400" />
            </div>
          </Field>

          {/* Optional marketing opt-in — unchecked by default, not required
              to complete the signup. Stored on the customer record. */}
          <label
            htmlFor="marketing_opt_in"
            className="flex items-start gap-3 rounded-2xl border border-charcoal-100 bg-cream-100 px-4 py-3 cursor-pointer hover:bg-cream-200/40 transition-colors"
          >
            <input
              id="marketing_opt_in"
              type="checkbox"
              checked={form.marketing_opt_in}
              onChange={(e) =>
                setForm((f) => ({ ...f, marketing_opt_in: e.target.checked }))
              }
              data-testid="register-marketing-opt-in"
              className="mt-1 w-4 h-4 accent-[#8B2252]"
            />
            <span className="text-sm text-charcoal-500">
              Send me newsletters and product updates
              <span className="block text-[11px] text-charcoal-400 mt-0.5">
                Optional — unsubscribe any time. We never share your email.
              </span>
            </span>
          </label>

          {error && (
            <p data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="register-submit"
            disabled={submitting}
            className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={2} />
            {submitting ? "Creating account…" : "Create account"}
          </button>

          <p className="text-center text-sm text-charcoal-400">
            Already have an account?{" "}
            <Link
              to="/login"
              data-testid="register-go-to-login"
              className="text-sage-700 hover:text-sage-800 font-medium"
            >
              Sign in
            </Link>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-charcoal-300 max-w-md mx-auto leading-relaxed">
          By creating an account you confirm you are 21+ and a qualified researcher. Compounds are
          for research use only and not for human consumption.
        </p>
      </div>
    </main>
  );
}

const Field = ({ label, htmlFor, hint, children }) => (
  <label htmlFor={htmlFor} className="block">
    <span className="block text-xs font-medium uppercase tracking-[0.18em] text-charcoal-400 mb-2">
      {label}
    </span>
    {children}
    {hint && <span className="block text-xs text-charcoal-300 mt-1.5">{hint}</span>}
  </label>
);
